import React from 'react';
import { Tag, Popover, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { HtmlContent } from '@client/utils/html_content';
import ItemsWithPopover from '@client/components/ui/table/ItemWithPopover';
import RiskSymbolDisplay from '@client/components/ui/RiskSymbolDisplay';
import TruncatedText from '@client/components/ui/TruncatedText';
import IriisLink from '@client/components/ui/IriisLink';
import { ProductTypeLegend } from '@client/components/ui/productLegends';
import { getArlaRatingStyle } from '@client/utils/arlaRating';

/**
 * Cell component for displaying CAS (Chemical Abstracts Service) numbers
 *
 * Renders a list of CAS numbers with popover functionality for long lists.
 * Automatically handles overflow and provides clean display.
 *
 * @param {Object} props - Component props
 * @param {string[]} props.casNumbers - Array of CAS number strings
 * @returns {JSX.Element} Formatted CAS numbers cell
 *
 * @example
 * <CasNumbersCell casNumbers={['123-45-6', '789-01-2']} />
 */
export const CasNumbersCell = ({ casNumbers }) => (
  <ItemsWithPopover items={casNumbers} getItemName={(cas) => cas} />
);

/**
 * Cell component for displaying category lists with name properties
 *
 * Generic component for any category-type data that has a 'name' property.
 * Handles multiple categories with consistent formatting.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.categories - Array of category objects
 * @param {string} props.categories[].name - Category name
 * @returns {JSX.Element} Formatted categories cell
 *
 * @example
 * <CategoriesCell categories={[{name: 'Herbicide'}, {name: 'Fungicide'}]} />
 */
export const CategoriesCell = ({ categories, label = 'name' }) => (
  <ItemsWithPopover items={categories} getItemName={(cat) => cat?.[label]} />
);

/**
 * Cell component for displaying pesticide groups from association data
 *
 * Extracts and displays pesticide group names from complex association objects.
 * Handles nested data structure safely with null checking.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of pesticide group associations
 * @param {Object} props.associations[].pesticide_group - Pesticide group object
 * @param {string} props.associations[].pesticide_group.name - Group name
 * @returns {JSX.Element} Formatted pesticide groups cell
 *
 * @example
 * <PesticideGroupsCell associations={[{pesticide_group: {name: 'Group A'}}]} />
 */
export const PesticideGroupsCell = ({ associations }) => (
  <ItemsWithPopover
    items={associations}
    getItemName={(association) => association.pesticide_group?.name}
    isNested={true}
  />
);

/**
 * Cell component for displaying chemical families from association data
 *
 * Filters associations to only show those with chemical family data,
 * then displays the family labels in a clean format.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of chemical family associations
 * @param {Object} props.associations[].chemical_family - Chemical family object
 * @param {string} props.associations[].chemical_family.label - Family label
 * @returns {JSX.Element} Formatted chemical families cell
 *
 * @example
 * <ChemicalFamiliesCell associations={[{chemical_family: {label: 'Organochlorine'}}]} />
 */
export const ChemicalFamiliesCell = ({ associations }) => {
  const familiesWithData =
    associations?.filter((assoc) => assoc.chemical_family) || [];

  return (
    <ItemsWithPopover
      items={familiesWithData}
      getItemName={(association) => association.chemical_family?.label}
    />
  );
};

/**
 * Cell component for displaying resistance development risk information
 *
 * Extracts resistance risk data from nested association structures.
 * Only displays associations that have resistance development data.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of pesticide associations
 * @param {Object} props.associations[].pesticide_group - Pesticide group object
 * @param {Object} props.associations[].pesticide_group.risk_of_resistance_development - Risk object
 * @param {string} props.associations[].pesticide_group.risk_of_resistance_development.name - Risk name
 * @returns {JSX.Element} Formatted resistance risk cell
 *
 * @example
 * <ResistanceRiskCell associations={[{pesticide_group: {risk_of_resistance_development: {name: 'High'}}}]} />
 */
export const ResistanceRiskCell = ({ associations }) => {
  const risksWithData =
    associations?.filter(
      (assoc) => assoc.pesticide_group?.risk_of_resistance_development
    ) || [];

  return (
    <ItemsWithPopover
      items={risksWithData}
      getItemName={(association) =>
        association.pesticide_group?.risk_of_resistance_development?.name
      }
    />
  );
};

/**
 * Cell component for displaying risk symbols
 *
 * Uses the dedicated RiskSymbolDisplay component to show risk symbols
 * with perfect alignment and consistent styling across the application.
 *
 * @param {Object} props - Component props
 * @param {Object|number} props.symbolData - Symbol data object or legacy integer value
 * @param {string} [props.iconSrc] - Fallback icon source URL (for legacy support)
 * @param {string} [props.alt='Risk symbol'] - Alt text for accessibility
 * @returns {JSX.Element} Risk symbol display
 *
 * @example
 * <RiskSymbolCell symbolData={{ calculated_value: 3, symbol: { image: "/media/symbol_image/3.png", label: "faible" } }} />
 */
export const RiskSymbolCell = ({
  symbolData,
  iconSrc,
  alt = 'Risk symbol',
  hideWhenEmpty = false,
  expertMarker = null,
  professionalTooltip = null,
  criteriaLabel = null,
  nameOverride = null,
}) => {
  return (
    <RiskSymbolDisplay
      symbolData={symbolData}
      size="sm"
      fallbackIcon={iconSrc}
      alt={alt}
      hasBackground={false}
      hideWhenEmpty={hideWhenEmpty}
      expertMarker={expertMarker}
      professionalTooltip={professionalTooltip}
      criteriaLabel={criteriaLabel}
      nameOverride={nameOverride}
    />
  );
};

/**
 * Cell component for rendering HTML content safely
 *
 * Wrapper around HtmlContent utility for consistent HTML rendering in tables.
 * Handles HTML sanitization and formatting automatically.
 *
 * @param {Object} props - Component props
 * @param {string} props.html - HTML string to render
 * @returns {JSX.Element} Safely rendered HTML content
 *
 * @example
 * <HtmlContentCell html="<strong>Substance Name</strong>" />
 */
export const HtmlContentCell = ({ html }) => <HtmlContent html={html} />;

/**
 * Cell component for rendering action links
 *
 * Generic component for creating consistent action links in table cells.
 * Supports customizable href, target, and styling.
 *
 * @param {Object} props - Component props
 * @param {string} props.href - Link destination URL
 * @param {string} [props.target='_blank'] - Link target behavior
 * @param {string} props.className - CSS classes for styling
 * @param {React.ReactNode} props.children - Link content/text
 * @returns {JSX.Element} Formatted action link
 *
 * @example
 * <ActionLinkCell
 *   href="/details/123"
 *   className="btn btn-primary"
 * >
 *   View Details
 * </ActionLinkCell>
 */
export const ActionLinkCell = ({
  href,
  target = '_blank',
  className,
  children,
}) => (
  <a href={href} target={target} className={className} rel="noreferrer">
    {children}
  </a>
);

/**
 * Cell component for displaying collections of items as tags
 *
 * Renders arrays of data as visually organized tag collections.
 * Supports custom key generation and name extraction functions.
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display as tags
 * @param {Function} props.getItemName - Function to extract display name from item
 * @param {Function} [props.getItemKey] - Function to generate unique keys
 * @returns {JSX.Element} Collection of formatted tags
 *
 * @example
 * <TagsCell
 *   items={sectors}
 *   getItemName={(sector) => sector.name}
 *   getItemKey={(sector) => sector.id}
 * />
 */
export const TagsCell = ({ items, getItemName, getItemKey }) => (
  <div className="flex flex-wrap gap-2">
    {items?.map((item, index) => (
      <Tag
        key={getItemKey ? getItemKey(item, index) : `tag-${index}`}
        size="small"
      >
        {getItemName(item)}
      </Tag>
    ))}
  </div>
);

const TreatmentProductItem = ({ treatmentProduct }) => {
  const { t } = useTranslation('common');
  const isSeries = treatmentProduct?.selection_type === 'series';
  const product = treatmentProduct?.product;
  const approvalNumber = product?.approval_number;
  const productTypes = product?.product_types;
  const productTypeNames = productTypes
    ?.map((pt) => pt?.name)
    .filter(Boolean)
    .join(', ');
  // For a series treatment-product the payload carries the series id/name in
  // `product`, so the link must point to the Fiche Série, not the product page.
  const href = isSeries
    ? `/series/${product?.id}/`
    : `/produits-commerciaux/details/${product?.id}/`;
  // Série hover: list the série's valid member products (B17).
  const members = isSeries ? (product?.member_products || []) : [];
  const nameLink = (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-[#105235] font-roboto text-[12px] font-bold leading-[22px] hover:underline hover:text-[#105235]"
    >
      <TruncatedText text={product?.name} maxLength={30} />
    </a>
  );

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        {members.length > 0 ? (
          <Tooltip
            title={(
              <div>
                <div className="font-semibold mb-1">
                  {t('legends.serieMemberProducts', 'Produits commerciaux de la série')}
                </div>
                <ul className="list-disc pl-4 mb-0">
                  {members.map((m) => (
                    <li key={m.id}>
                      {m.name}{m.approval_number ? ` (${m.approval_number})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          >
            {nameLink}
          </Tooltip>
        ) : nameLink}
        {approvalNumber && (
          <span className="font-roboto text-[12px] text-[rgba(0,0,0,0.85)] leading-[22px]">
            ({approvalNumber})
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {productTypeNames && (
          <span className="font-roboto text-[12px] text-[rgba(0,0,0,0.85)] leading-[22px]">
            ({productTypeNames})
          </span>
        )}
        {treatmentProduct?.selection_type === 'series' && (
          <ProductTypeLegend type="serie" />
        )}
        {product?.is_organic_approval && (
          <ProductTypeLegend type="biological" height={20} />
        )}
      </div>
    </div>
  );
};

const TreatmentProductVisibleContent = ({ items }) => (
  <div className="flex flex-col gap-2">
    {items.map((tp, index) => (
      <React.Fragment key={tp?.product?.id || index}>
        <TreatmentProductItem treatmentProduct={tp} />
        {index < items.length - 1 && (
          <span className="text-[#105235] font-bold text-[13px]">+</span>
        )}
      </React.Fragment>
    ))}
  </div>
);


export const TreatmentProductsCell = ({ TreatmentProducts, showPrescription = false, prescriptionComments = '' }) => {
  const validProducts = TreatmentProducts || [];

  if (!validProducts?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const prescriptionTag = (
    <Tag color="#FA8C16" className="rounded text-[9px] font-bold leading-none !mx-0 !px-1.5 !py-0.5 w-fit cursor-default" style={{ height: 18, display: 'inline-flex', alignItems: 'center' }}>
      PRESCRIPTION
    </Tag>
  );

  return (
    <div className="flex flex-col gap-2">
      <TreatmentProductVisibleContent items={validProducts} />
      {showPrescription ? (
        prescriptionComments ? (
          <Tooltip title={prescriptionComments} placement="top">
            {prescriptionTag}
          </Tooltip>
        ) : prescriptionTag
      ) : prescriptionComments ? (
        <Tooltip title={prescriptionComments} placement="top">
          <span className="text-[10px] italic text-gray-600 cursor-default truncate max-w-[180px]">
            {prescriptionComments}
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
};

const SubstanceDisplay = ({ substance }) => {
  const pesticideGroups = substance?.pesticide_group_associations
    ?.map((assoc) => assoc.pesticide_group?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <span className="inline items-center">
      <a
        href={`/matieres-actives/details/${substance?.id}/`}
        target="_blank"
        rel="noreferrer"
        className="text-[#105235] font-roboto text-[12px] font-bold leading-[22px] hover:underline hover:text-[#105235]"
      >
        <HtmlContent html={substance?.name} className="inline" />
      </a>
      {pesticideGroups && (
        <span className="font-roboto text-[12px] font-normal text-[rgba(0,0,0,0.85)] leading-[22px]">
          ({pesticideGroups})
        </span>
      )}
    </span>
  );
};

const ProductSubstancesGroup = ({ treatmentProduct }) => {
  const activeSubstances = treatmentProduct?.product?.active_substances;
  if (!activeSubstances?.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {activeSubstances.map((activeSubstanceProduct, asIndex) => {
        const substance = activeSubstanceProduct?.active_substance;
        if (!substance) return null;

        return (
          <React.Fragment key={substance?.id || asIndex}>
            {asIndex > 0 && (
              <span className="text-[#105235] font-bold text-[12px] mx-0.5">/</span>
            )}
            <SubstanceDisplay substance={substance} />
          </React.Fragment>
        );
      })}
    </div>
  );
};

const ActiveSubstancesVisibleContent = ({ items }) => (
  <div className="flex flex-col gap-2">
    {items.map((tp, index) => (
      <React.Fragment key={tp?.product?.id || index}>
        <ProductSubstancesGroup treatmentProduct={tp} />
        {index < items.length - 1 && (
          <span className="text-[#105235] font-bold text-[12px]">+</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const ActiveSubstancesPopoverContent = ({ items }) => (
  <div className="flex flex-col max-w-lg">
    {items.map((tp, index) => (
      <div
        key={tp?.product?.id || index}
        className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
      >
        <ProductSubstancesGroup treatmentProduct={tp} />
      </div>
    ))}
  </div>
);

export const ActiveSubstancesCell = ({ record }) => {
  const validProducts = record?.treatment_products || [];

  if (!validProducts?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  // Collect unique active substances across all products to avoid duplicates
  const substancesMap = new Map();
  validProducts.forEach((tp) => {
    tp?.product?.active_substances?.forEach((asp) => {
      const substance = asp?.active_substance;
      if (substance?.id && !substancesMap.has(substance.id)) {
        substancesMap.set(substance.id, substance);
      }
    });
  });

  const uniqueSubstances = Array.from(substancesMap.values());

  if (!uniqueSubstances.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const items = uniqueSubstances.map((s) => ({ ...s, id: s.id, active_substance: s }));

  return (
    <ItemsWithPopover
      items={items}
      getItemName={(item) => item?.name}
      maxVisible={1}
      CustomVisibleContent={ProductSubstancesVisibleContent}
      CustomPopoverContent={ProductSubstancesPopoverContent}
    />
  );
};

const ProductSubstancesVisibleContent = ({ items }) => (
  <div className="flex flex-wrap items-center gap-1">
    {items.map((asp, index) => {
      const substance = asp?.active_substance;
      if (!substance) return null;
      return (
        <React.Fragment key={substance?.id || index}>
          {index > 0 && (
            <span className="text-[#105235] font-bold text-[12px] mx-0.5">/</span>
          )}
          <SubstanceDisplay substance={substance} />
        </React.Fragment>
      );
    })}
  </div>
);

const ProductSubstancesPopoverContent = ({ items }) => (
  <div className="flex flex-col max-w-lg">
    {items.map((asp, index) => {
      const substance = asp?.active_substance;
      if (!substance) return null;
      return (
        <div
          key={substance?.id || index}
          className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
        >
          <SubstanceDisplay substance={substance} />
        </div>
      );
    })}
  </div>
);

export const ProductActiveSubstancesCell = ({ activeSubstances }) => {
  if (!activeSubstances?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const items = activeSubstances
    .filter((asp) => asp?.active_substance)
    .map((asp) => ({ ...asp, id: asp?.active_substance?.id }));

  if (!items.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <ItemsWithPopover
      items={items}
      getItemName={(asp) => asp?.active_substance?.name}
      maxVisible={1}
      CustomVisibleContent={ProductSubstancesVisibleContent}
      CustomPopoverContent={ProductSubstancesPopoverContent}
    />
  );
};

export const CombinedDelayCell = ({ text, separator = '/' }) => {
  if (!text) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const items = text
    .split(separator)
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v, i) => ({ id: `${i}`, name: v }));

  if (!items.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <span className="font-bold text-[#2D5F2B] hover:underline cursor-pointer">
      <ItemsWithPopover
        items={items}
        getItemName={(item) => item.name}
        maxVisible={1}
      />
    </span>
  );
};

export const SimpleTextCell = ({ text }) =>
  text ? (
    <TruncatedText text={text} maxLength={100} />
  ) : (
    <span className="text-gray-400 text-xs italic font-light">N/A</span>
  );

const ProductActivityTypesGroup = ({ treatmentProduct }) => {
  const activityTypesMap = new Map();
  treatmentProduct?.product?.active_substances?.forEach((asp) => {
    asp?.active_substance?.activity_types?.forEach((at) => {
      if (at?.short_name && !activityTypesMap.has(at.id)) {
        activityTypesMap.set(at.id, at);
      }
    });
  });
  const types = Array.from(activityTypesMap.values());
  if (!types.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {types.map((at, i) => (
        <React.Fragment key={at.id}>
          {i > 0 && (
            <span className="text-[#105235] font-bold text-[12px] mx-0.5">/</span>
          )}
          <span className="font-roboto text-[12px] text-[rgba(0,0,0,0.85)] leading-[22px]">
            <HtmlContent html={at.short_name} className="inline" />
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const ActivityTypesVisibleContent = ({ items }) => (
  <div className="flex flex-col gap-2">
    {items.map((tp, index) => (
      <React.Fragment key={tp?.product?.id || index}>
        <ProductActivityTypesGroup treatmentProduct={tp} />
        {index < items.length - 1 && (
          <span className="text-[#105235] font-bold text-[12px]">+</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const ActivityTypesPopoverContent = ({ items }) => (
  <div className="flex flex-col max-w-lg">
    {items.map((tp, index) => (
      <div
        key={tp?.product?.id || index}
        className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
      >
        <ProductActivityTypesGroup treatmentProduct={tp} />
      </div>
    ))}
  </div>
);

export const ActivityTypesCell = ({ treatmentProducts }) => {
  const validProducts = treatmentProducts || [];

  if (!validProducts?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  // Collect unique activity types across all products to avoid duplicates
  const activityTypesMap = new Map();
  validProducts.forEach((tp) => {
    tp?.product?.active_substances?.forEach((asp) => {
      asp?.active_substance?.activity_types?.forEach((at) => {
        if (at?.short_name && !activityTypesMap.has(at.id)) {
          activityTypesMap.set(at.id, at);
        }
      });
    });
  });

  const uniqueTypes = Array.from(activityTypesMap.values());

  if (!uniqueTypes.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {uniqueTypes.map((at, i) => (
        <React.Fragment key={at.id}>
          {i > 0 && (
            <span className="text-[#105235] font-bold text-[12px] mx-0.5">/</span>
          )}
          <span className="font-roboto text-[12px] text-[rgba(0,0,0,0.85)] leading-[22px]">
            <HtmlContent html={at.short_name} className="inline" />
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Popover content for enemies and activity types — matches product details design.
 * Groups enemies by category, shows ARLA ratings with color badges.
 */
const EnemiesActivityTypesPopoverContent = ({ treatmentEnemies, treatmentProducts, iriisLinks }) => {
  // Collect activity types per active substance (like product details page)
  const substanceActivityTypes = [];
  const seenSubstances = new Set();
  (treatmentProducts || []).forEach((tp) => {
    tp?.product?.active_substances?.forEach((asp) => {
      const substance = asp?.active_substance;
      if (!substance || seenSubstances.has(substance.id)) return;
      seenSubstances.add(substance.id);
      const types = (substance.activity_types || [])
        .map((at) => at?.activity_type?.label || at?.short_name || at?.label)
        .filter(Boolean);
      if (substance.name || types.length > 0) {
        substanceActivityTypes.push({ id: substance.id, name: substance.name, types });
      }
    });
  });

  // Collect enemies (deduplicated)
  const enemies = [];
  const seenEnemies = new Set();
  (treatmentEnemies || []).forEach((te) => {
    const enemy = te?.enemy;
    if (!enemy || seenEnemies.has(enemy.id)) return;
    seenEnemies.add(enemy.id);
    enemies.push({
      id: enemy.id,
      name: enemy.client_display_name || enemy.french_name || enemy.english_name || enemy.latin_name,
      latinName: enemy.latin_name,
      category: enemy.category?.name,
      rating: te.enemy_rating_ARLA?.name,
      ratingColor: te.enemy_rating_ARLA?.display_color,
      iriisUrl: iriisLinks?.[enemy.id]?.url,
    });
  });

  return (
    <div className="max-w-lg max-h-[400px] overflow-y-auto">
      {/* Type d'activité — per active substance */}
      {substanceActivityTypes.length > 0 && (
        <div className="border-b border-gray-200">
          <div className="border border-gray-200 rounded-[8px] overflow-hidden m-3">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F5F5F5]">
                  <th className="text-[12px] font-bold text-[#000] text-left py-2 px-3">Matières actives</th>
                  <th className="text-[12px] font-bold text-[#000] text-left py-2 px-3">Type d&apos;activité</th>
                </tr>
              </thead>
              <tbody>
                {substanceActivityTypes.map((sa) => (
                  <tr key={sa.id} className="border-t border-gray-200">
                    <td className="text-[12px] font-medium text-[#105235] py-1.5 px-3"><HtmlContent html={sa.name} /></td>
                    <td className="text-[12px] text-[#000] py-1.5 px-3">
                      {sa.types.length > 0 ? sa.types.join(', ') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ennemis ciblés par ce traitement phytosanitaire selon l'étiquette */}
      {enemies.length > 0 && (
        <div className="m-3">
          <h5 className="text-[12px] font-semibold text-[#000] mb-2">
            Ennemis ciblés par ce traitement phytosanitaire selon l&apos;étiquette
          </h5>
          <div className="border border-gray-200 rounded-[8px] overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F5F5F5]">
                  <th className="text-[12px] font-bold text-[#000] text-left py-2 px-3">Ennemis (Liste globale)</th>
                  <th className="text-[12px] font-bold text-[#000] text-left py-2 px-3">Efficacité selon l&apos;étiquette</th>
                  <th className="text-[12px] font-bold text-[#000] text-left py-2 px-3">Efficacité selon le groupe d&apos;experts</th>
                </tr>
              </thead>
              <tbody>
                {enemies.map((enemy) => (
                  <tr key={enemy.id} className="border-t border-gray-200">
                    <td className="text-[12px] text-[#000] py-1.5 px-3">
                      {enemy.category && (
                        <>
                          <span className="text-gray-500 text-[10px]">{enemy.category}</span>
                          <br />
                        </>
                      )}
                      <span className="font-bold text-[#105235]">
                        <IriisLink url={enemy.iriisUrl}>{enemy.name}</IriisLink>
                      </span>
                      {enemy.latinName && enemy.latinName !== enemy.name && (
                        <span className="text-gray-400 italic text-[10px]"> / {enemy.latinName}</span>
                      )}
                    </td>
                    <td className="text-[12px] py-1.5 px-3 text-left">
                      {enemy.rating ? (
                        <span
                          className="inline-block px-2 py-0.5 rounded text-[11px] font-medium"
                          style={getArlaRatingStyle(enemy.ratingColor)}
                        >
                          {enemy.rating}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="text-[12px] py-1.5 px-3 text-left">-</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!enemies.length && !substanceActivityTypes.length && (
        <div className="px-4 py-3 text-xs text-gray-400 italic">N/A</div>
      )}
    </div>
  );
};

/**
 * Cell for "Ennemis et types d'activités" column in rapide mode.
 * Shows a summary count that opens a popover with full details.
 */
export const EnemiesAndActivityTypesCell = ({ treatmentEnemies, treatmentProducts, iriisLinks }) => {
  const hasEnemies = treatmentEnemies?.some((te) => te?.enemy);
  const hasActivityTypes = (treatmentProducts || []).some((tp) =>
    tp?.product?.active_substances?.some((asp) =>
      asp?.active_substance?.activity_types?.length > 0
    )
  );

  if (!hasEnemies && !hasActivityTypes) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <Popover
      content={
        <EnemiesActivityTypesPopoverContent
          treatmentEnemies={treatmentEnemies}
          treatmentProducts={treatmentProducts}
          iriisLinks={iriisLinks}
        />
      }
      trigger="click"
      placement="left"
      overlayStyle={{ maxWidth: 450 }}
    >
      <button
        type="button"
        aria-label="Voir ennemis et types d'activités"
        className="flex items-center justify-center rounded-lg border border-[#D9D9D9] bg-white cursor-pointer transition-all hover:border-[#7CB305] hover:bg-[#F6FFED]"
        style={{ width: '34px', height: '34px', borderRadius: '8px', border: '1px solid #D9D9D9', background: '#fff' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#595959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      </button>
    </Popover>
  );
};

// ========================
// EXTRACTION TABLE CELLS
// ========================

const NA = <span className="text-gray-400 text-xs italic font-light">N/A</span>;

/**
 * Renders "name_doseMin à doseMax unit" per product, "+" between mixture products.
 * Series products show "(série)" suffix.
 */
export const ProductWithDoseCell = ({ treatmentProducts }) => {
  if (!treatmentProducts?.length) return NA;

  const getProductLabel = (tp) => {
    const name = tp?.product?.name || '';
    const isSeries = tp?.selection_type === 'series';
    let doseStr = '';
    if (Number(tp.min_dose) && Number(tp.max_dose) && Number(tp.min_dose) !== Number(tp.max_dose)) {
      doseStr = `${tp.min_dose} à ${tp.max_dose}`;
    } else if (Number(tp.max_dose)) {
      doseStr = `${tp.max_dose}`;
    } else if (Number(tp.min_dose)) {
      doseStr = `${tp.min_dose}`;
    }
    const unit = tp.units?.name || tp.units || '';
    const dosePart = doseStr ? `_${doseStr}${unit ? ` ${unit}` : ''}` : '';
    return `${name}${isSeries ? ' (série)' : ''}${dosePart}`;
  };

  return (
    <ItemsWithPopover
      items={treatmentProducts.map((tp, i) => ({ ...tp, id: tp?.product?.id || i }))}
      getItemName={getProductLabel}
      maxVisible={1}
    />
  );
};

/**
 * Renders approval/registration numbers, "+" between mixture products.
 */
export const ApprovalNumberCell = ({ treatmentProducts }) => {
  if (!treatmentProducts?.length) return NA;

  const numbers = treatmentProducts
    .map((tp) => tp?.product?.approval_number || '')
    .filter(Boolean);

  if (!numbers.length) return NA;
  return <span className="text-xs">{numbers.join(' + ')}</span>;
};

/**
 * Generic cell for combining a field across products.
 * Uses "/" within same product (multiple substances), "+" between products.
 *
 * @param {Function} extractItems - (treatmentProduct) => string[]
 */
export const CombinedProductFieldCell = ({ treatmentProducts, extractItems }) => {
  if (!treatmentProducts?.length) return NA;

  const parts = treatmentProducts.map((tp) => {
    const items = extractItems(tp);
    return items.filter(Boolean).join(' / ');
  }).filter(Boolean);

  if (!parts.length) return NA;
  return <span className="text-xs">{parts.join(' + ')}</span>;
};

/**
 * Shows approval status name, with "(NOUVEAU)" suffix if approved less than 1 year ago.
 */
export const ApprovalStatusCell = ({ record, nouveauLabel = '(NOUVEAU)' }) => {
  const status = record?.approval_status;
  if (!status?.name) return NA;

  let isNouveau = false;
  if (status.name === 'Actif' && record.approval_date) {
    const approvalDate = new Date(record.approval_date);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    isNouveau = approvalDate > oneYearAgo;
  }

  return (
    <span className="text-xs">
      {status.name}
      {isNouveau && <span className="text-green-600 font-bold ml-1">{nouveauLabel}</span>}
    </span>
  );
};

const EnemiesVisibleContent = ({ items }) => (
  <div className="flex flex-col gap-1">
    {items.map((enemy, index) => (
      <span key={enemy.id || index}>
        <IriisLink url={enemy.iriisUrl}>
          <TruncatedText text={enemy.name} maxLength={30} />
        </IriisLink>
      </span>
    ))}
  </div>
);

const EnemiesPopoverContent = ({ items }) => (
  <div className="flex flex-col max-w-lg">
    {items.map((enemy, index) => (
      <div
        key={index}
        className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
      >
        <div className="font-medium text-sm text-gray-900 leading-relaxed mb-1">
          <IriisLink url={enemy.iriisUrl}>{enemy.name}</IriisLink>
        </div>
        {enemy.rating && (
          <span
            className="inline-block text-xs px-2 py-0.5 rounded-md text-white mt-1"
            style={{ backgroundColor: enemy.ratingColor || '#8C8C8C' }}
          >
            {enemy.rating}
          </span>
        )}
      </div>
    ))}
  </div>
);

export const EnemiesCell = ({ treatmentEnemies, filteredEnemyIds, iriisLinks }) => {
  if (!treatmentEnemies?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const enemiesMap = new Map();

  treatmentEnemies.forEach((te) => {
    const enemy = te?.enemy;
    if (enemy && !enemiesMap.has(enemy.id)) {
      // Only include enemies that match the filtered IDs
      if (filteredEnemyIds?.length > 0 && !filteredEnemyIds.includes(enemy.id)) {
        return;
      }
      enemiesMap.set(enemy.id, {
        id: enemy.id,
        name: enemy.client_display_name || enemy.french_name || enemy.english_name || enemy.latin_name,
        iriisUrl: iriisLinks?.[enemy.id]?.url,
        rating: te.enemy_rating_ARLA?.name || null,
        ratingColor: te.enemy_rating_ARLA?.display_color || null,
      });
    }
  });

  const enemies = Array.from(enemiesMap.values());

  if (enemies.length === 0) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <ItemsWithPopover
      items={enemies}
      getItemName={(enemy) => enemy.name}
      maxVisible={1}
      isNested={true}
      CustomVisibleContent={EnemiesVisibleContent}
      CustomPopoverContent={EnemiesPopoverContent}
    />
  );
};
